import type { DriveStep, DriverHook } from "driver.js";
import type { TourSidebarPanel } from "./types";

export interface DesktopTourStepCopy {
  closingDescription: string;
  closingTitle: string;
  coachSuggestionsDescription: string;
  coachSuggestionsTitle: string;
  composerDescription: string;
  composerTitle: string;
  historyDescription: string;
  historyTitle: string;
  savedPromptsDescription: string;
  savedPromptsTitle: string;
  scopeDescription: string;
  scopeTitle: string;
  settingsDescription: string;
  settingsTitle: string;
  sidebarDescription: string;
  sidebarTitle: string;
  welcomeDescription: string;
  welcomeTitle: string;
}

interface DesktopTourStepController {
  collapseSidebar: () => void;
  selectSidebarPanel: (panel: TourSidebarPanel) => void;
}

interface DesktopTourStepOptions {
  controller: DesktopTourStepController;
  copy: DesktopTourStepCopy;
}

const createPanelTransition =
  (
    controller: DesktopTourStepController,
    panel: TourSidebarPanel,
    direction: "next" | "previous",
  ): DriverHook =>
  (_element, _step, hookOptions) => {
    controller.selectSidebarPanel(panel);
    if (direction === "next") {
      hookOptions.driver.moveNext();
      return;
    }

    hookOptions.driver.movePrevious();
  };

const createComposerTransition =
  (
    controller: DesktopTourStepController,
    direction: "next" | "previous",
  ): DriverHook =>
  (_element, _step, hookOptions) => {
    controller.collapseSidebar();
    if (direction === "next") {
      hookOptions.driver.moveNext();
      return;
    }

    hookOptions.driver.movePrevious();
  };

export const createCoachInterfaceIntroDesktopSteps = ({
  controller,
  copy,
}: DesktopTourStepOptions): DriveStep[] => [
  {
    data: { tourStep: "welcome" },
    popover: {
      title: copy.welcomeTitle,
      description: copy.welcomeDescription,
      showButtons: ["next", "close"],
    },
  },
  {
    data: { tourStep: "scope" },
    element: '[data-tour="scope-controls"]',
    waitForElement: 1_000,
    popover: {
      title: copy.scopeTitle,
      description: copy.scopeDescription,
      side: "bottom",
      align: "start",
    },
  },
  {
    data: { tourStep: "sidebar-rail" },
    element: '[data-tour="sidebar-rail"]',
    waitForElement: 1_000,
    popover: {
      title: copy.sidebarTitle,
      description: copy.sidebarDescription,
      side: "right",
      align: "start",
      onNextClick: createPanelTransition(controller, "history", "next"),
    },
  },
  {
    data: { tourStep: "history" },
    element: '[data-tour="history-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.historyTitle,
      description: copy.historyDescription,
      side: "right",
      align: "start",
      onNextClick: createPanelTransition(controller, "saved-prompts", "next"),
      onPrevClick: createPanelTransition(controller, "history", "previous"),
    },
  },
  {
    data: { tourStep: "saved-prompts" },
    element: '[data-tour="saved-prompts-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.savedPromptsTitle,
      description: copy.savedPromptsDescription,
      side: "right",
      align: "start",
      onNextClick: createPanelTransition(
        controller,
        "coach-suggestions",
        "next",
      ),
      onPrevClick: createPanelTransition(controller, "history", "previous"),
    },
  },
  {
    data: { tourStep: "coach-suggestions" },
    element: '[data-tour="coach-suggestions-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.coachSuggestionsTitle,
      description: copy.coachSuggestionsDescription,
      side: "right",
      align: "start",
      onNextClick: createPanelTransition(controller, "settings", "next"),
      onPrevClick: createPanelTransition(
        controller,
        "saved-prompts",
        "previous",
      ),
    },
  },
  {
    data: { tourStep: "settings" },
    element: '[data-tour="settings-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.settingsTitle,
      description: copy.settingsDescription,
      side: "right",
      align: "start",
      onNextClick: createComposerTransition(controller, "next"),
      onPrevClick: createPanelTransition(
        controller,
        "coach-suggestions",
        "previous",
      ),
    },
  },
  {
    data: { tourStep: "composer" },
    element: '[data-tour="message-composer"]',
    waitForElement: 1_000,
    popover: {
      title: copy.composerTitle,
      description: copy.composerDescription,
      side: "top",
      align: "center",
      onNextClick: createPanelTransition(controller, "guided-tours", "next"),
      onPrevClick: createPanelTransition(controller, "settings", "previous"),
    },
  },
  {
    data: { tourStep: "guided-tours" },
    element: '[data-tour="guided-tours-panel"]',
    waitForElement: 1_000,
    popover: {
      title: copy.closingTitle,
      description: copy.closingDescription,
      side: "right",
      align: "start",
      onPrevClick: createComposerTransition(controller, "previous"),
    },
  },
];
